import { HttpClient } from '@adapters/HttpClient';
import { ShopsModel } from '../models/Shops';
// import { PaymentMethod } from '../hooks/useSettingsManager'; // eslint-disable-line @typescript-eslint/no-unused-vars

export interface ShopsFilters {
  searchTerm?: string;
  advanced_filters?: {
    filters: { column: string; operator: string; value: string | string[] }[];
    logic: 'AND' | 'OR';
  };
  /** Filtrar por equipo: se envía vía `advanced_filters` (columna `team_id`, operador `in`). */
  team_id?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface ShopsPage {
  shops: ShopsModel[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

// ========================================
// NUEVAS INTERFACES PARA ACTUALIZACIÓN
// ========================================

export interface UpdateShopSettingsRequest {
  name: string;
  fulfillment: boolean;
  default_payment_method: number | null;
  approval_type: number;
  linked_payment_methods: {
    id?: number;
    title: string;
    payment_method_id: number;
  }[];
}

export interface UpdateShopSettingsResponse {
  success: boolean;
  message?: string;
  data?: ShopsModel;
}

export interface ImportFiltersRequest {
  import_filters: {
    field: string;
    operator: string;
    value: string;
  }[];
}

export class ShopsApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * Obtiene una tienda por id (GET /shops/{id}).
   */
  async getShopById(id: number): Promise<{ id: number; name: string } | null> {
    try {
      const response = await this.httpClient.get<any>(`/shops/${id}`);
      const raw = response?.data ?? response;
      if (raw == null || raw.id == null) return null;
      return { id: Number(raw.id), name: String(raw.name ?? '') };
    } catch {
      return null;
    }
  }

  async getShopsPaginated(filters: ShopsFilters = {}): Promise<ShopsPage> {
    const {
      searchTerm = '',
      advanced_filters,
      team_id,
      sortBy = 'id',
      sortDirection = 'desc',
      page = 1,
      limit = 10,
    } = filters;

    const params: any = {
      ...(sortBy && { order_by: sortBy }),
      ...(sortDirection && { order_direction: sortDirection }),
      page,
      per_page: limit,
    };

    // 🎯 Construir advanced_filters combinando búsqueda y filtros
    // Cambio realizado: Migrar de parámetros individuales a advanced_filters para consistencia
    // con el resto del proyecto. Esto permite filtros más flexibles y mantiene la estructura
    // estándar del API
    const allFilters: {
      column: string;
      operator: string;
      value: string | string[] | number[];
    }[] = [];

    // GET /api/v1/shops: team_id vía advanced_filters, operador `in` (SearchShopDTO)
    if (typeof team_id === 'number' && team_id > 0) {
      allFilters.push({
        column: 'team_id',
        operator: 'in',
        value: [team_id],
      });
    }

    // 🎯 Agregar filtro de búsqueda por nombre si existe
    if (searchTerm) {
      allFilters.push({
        column: 'name',
        operator: 'contains',
        value: searchTerm,
      });
    }

    // 🎯 Agregar filtros avanzados si existen
    if (advanced_filters?.filters) {
      allFilters.push(...advanced_filters.filters);
    }

    // 🎯 Serializar advanced_filters como query params anidados
    // Estructura: advanced_filters[filters][0][column] = 'status'
    // Operador: 'equals' para filtros exactos
    // Lógica: 'AND' para combinar múltiples filtros
    if (allFilters.length > 0) {
      allFilters.forEach((filter, index) => {
        params[`advanced_filters[filters][${index}][column]`] = filter.column;
        params[`advanced_filters[filters][${index}][operator]`] = filter.operator;

        // 🎯 Manejar valores de array para operadores 'in' y 'not_in'
        if (filter.operator === 'in' || filter.operator === 'not_in') {
          // Si el valor es un array, enviar cada elemento
          if (Array.isArray(filter.value)) {
            filter.value.forEach((val, valIndex) => {
              params[`advanced_filters[filters][${index}][value][${valIndex}]`] = val;
            });
          } else {
            // Si es un string, convertirlo a array con un elemento
            params[`advanced_filters[filters][${index}][value][0]`] = filter.value;
          }
        } else {
          // Para otros operadores, enviar el valor como string
          params[`advanced_filters[filters][${index}][value]`] = filter.value;
        }
      });
      params['advanced_filters[logic]'] = advanced_filters?.logic || 'AND';
    }

    // Solo mostrar loading global para la primera página
    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<any>('/shops', params, {}, showGlobalLoading);

    return {
      shops: response.data,
      nextPage: response.meta.currentPage < response.meta.lastPage ? response.meta.currentPage + 1 : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }

  async getAll(): Promise<ShopsModel[]> {
    const response = await this.getShopsPaginated();
    return response.shops;
  }

  /**
   * 🎯 Eliminar una tienda según su channel_id
   * @param shopId - ID de la tienda a eliminar
   * @param channelId - ID del canal (1=Shopify, 3=WooCommerce)
   * @returns Promise con la respuesta de la API
   */
  async deleteShop(
    shopId: number,
    _channelId?: number, // eslint-disable-line @typescript-eslint/no-unused-vars
  ): Promise<{ success: boolean; message: string }> {
    try {
      // 🎯 Nuevo endpoint unificado
      const endpoint = `/shops/delete/${shopId}`;

      const response = await this.httpClient.delete(endpoint);

      return {
        success: true,
        message: (response as any)?.message || 'Tienda eliminada exitosamente',
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error al eliminar la tienda',
      };
    }
  }

  /**
   * 🎯 Activar o desactivar una tienda
   * @param shopId - ID de la tienda a activar/desactivar
   * @returns Promise con la respuesta de la API
   */
  async toggleShopStatus(shopId: number): Promise<{ success: boolean; message: string }> {
    try {
      const endpoint = `/shops/${shopId}/toggle-status`;
      const response = await this.httpClient.patch(endpoint, {});

      return {
        success: true,
        message: (response as any)?.message || 'Estado de la tienda actualizado exitosamente',
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error al cambiar el estado de la tienda',
      };
    }
  }

  /**
   * 🎯 Actualizar solo el nombre de una tienda
   * @param shopId - ID de la tienda a actualizar
   * @param name - Nuevo nombre de la tienda
   * @returns Promise con la respuesta de la API
   */
  async updateShopName(shopId: number, name: string): Promise<{ success: boolean; message: string }> {
    try {
      const endpoint = `/shops/${shopId}/name`;
      const payload = { name };

      await this.httpClient.put(endpoint, payload);

      return {
        success: true,
        message: 'Nombre de la tienda actualizado exitosamente',
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error al actualizar el nombre de la tienda',
      };
    }
  }

  /**
   * 🎯 Actualizar los ajustes de una tienda
   * @param shopId - ID de la tienda a actualizar
   * @param settings - Datos de los ajustes a enviar
   * @returns Promise con la respuesta de la API
   */
  async updateShopSettings(shopId: number, settings: UpdateShopSettingsRequest): Promise<UpdateShopSettingsResponse> {
    try {
      const endpoint = `/shops/${shopId}`;

      const response = await this.httpClient.put<UpdateShopSettingsResponse>(endpoint, settings);

      // Si la llamada no lanza error pero la respuesta es vacía (204 No Content),
      // o no tiene la propiedad `success`, lo consideramos un éxito.
      if (!response || typeof response.success === 'undefined') {
        return { success: true, message: 'Ajustes guardados exitosamente' };
      }

      return response;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error al guardar los ajustes',
      };
    }
  }

  /**
   * 🎯 Actualizar los filtros de importación de una tienda
   * @param shopId - ID de la tienda a actualizar
   * @param filters - Datos de los filtros a enviar
   * @returns Promise con la respuesta de la API
   */
  async updateImportFilters(
    shopId: number,
    filters: ImportFiltersRequest,
  ): Promise<{ success: boolean; message: string }> {
    try {
      const endpoint = `/shops/${shopId}/import-filters`;

      await this.httpClient.put(endpoint, filters);

      return {
        success: true,
        message: 'Filtros actualizados correctamente',
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error al actualizar filtros',
      };
    }
  }
}
